import { Card, Title, BarChart } from '@tremor/react';
import { formatMxn } from '../lib/format';
import { t } from '../lib/i18n';
import type { SalesByStorePoint } from '../api/types';

interface Props {
  rows: SalesByStorePoint[];
  /** Called with (sid, store_name) when the user clicks a bar. Optional —
   *  unset means no click affordance. */
  onSelectStore?: (sid: number, storeName: string) => void;
}

export default function SalesByStoreChart({ rows, onSelectStore }: Props) {
  // The parent (Overview) governs whether we render at all (empty data → grid
  // cell omitted entirely). Don't double-guard here.
  // The `[t.series.revenue]` key is intentionally the same string as the
  // `categories` value below — Tremor v3 has no separate legend label, so the
  // i18n string IS the data key. If we ever go bilingual, swap to a custom
  // legend renderer.
  // _sid rides along on each datum so the click handler can resolve the store
  // back to its sid without a name-to-sid lookup map.
  const data = rows.map((r) => ({
    store: r.store_name,
    _sid: r.sid,
    [t.series.revenue]: r.revenue_mxn,
  }));
  return (
    <Card>
      <Title>{t.charts.salesByStoreTitle}</Title>
      <BarChart
        className="mt-4 h-72"
        data={data}
        index="store"
        categories={[t.series.revenue]}
        colors={['blue']}
        valueFormatter={formatMxn}
        yAxisWidth={72}
        onValueChange={(v) => {
          if (!onSelectStore || !v) return;
          const payload = v as unknown as { _sid?: number; store?: string };
          if (typeof payload._sid === 'number') {
            onSelectStore(payload._sid, payload.store ?? '');
          }
        }}
      />
    </Card>
  );
}
